import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Button, OrganizerInputClock9 } from "@mebi/ui";
import { BaseInput, type BaseInputProps } from "./BaseInput";
import { WheelPicker } from "./WheelPicker";
import { cn } from "@/lib/utils";

export interface TimeInputProps extends Pick<
  BaseInputProps,
  | "name"
  | "id"
  | "label"
  | "placeholder"
  | "error"
  | "helperText"
  | "disabled"
  | "className"
  | "containerClassName"
  | "labelClassName"
  | "required"
> {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
}

const DEFAULT_TIME = { hours: "08", minutes: "00" };

const parseTimeValue = (value?: string) => {
  const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(value?.trim() || "");
  if (!match) {
    return DEFAULT_TIME;
  }

  return {
    hours: match[1].padStart(2, "0"),
    minutes: match[2],
  };
};

const buildSyntheticChangeEvent = (
  name: string | undefined,
  value: string,
): React.ChangeEvent<HTMLInputElement> =>
  ({
    target: { value, name },
    currentTarget: { value, name },
  }) as unknown as React.ChangeEvent<HTMLInputElement>;

const buildSyntheticBlurEvent = (
  name: string | undefined,
  value: string,
): React.FocusEvent<HTMLInputElement> =>
  ({
    target: { name, value },
    currentTarget: { name, value },
    type: "blur",
  }) as unknown as React.FocusEvent<HTMLInputElement>;

const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>((props, ref) => {
  const {
    id,
    name,
    label,
    placeholder,
    error,
    helperText,
    disabled,
    className,
    containerClassName,
    labelClassName,
    required,
    value: propValue,
    onChange,
    onBlur,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [draftHours, setDraftHours] = useState(DEFAULT_TIME.hours);
  const [draftMinutes, setDraftMinutes] = useState(DEFAULT_TIME.minutes);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showModal || !inputContainerRef.current) return;

    const body = document.body;
    const root = document.documentElement;
    const rootScrollY = window.scrollY;
    const bodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      width: body.style.width,
      overflowY: body.style.overflowY,
      overscrollBehaviorY: body.style.overscrollBehaviorY,
    };
    const rootStyles = {
      overflowY: root.style.overflowY,
      overscrollBehaviorY: root.style.overscrollBehaviorY,
    };

    body.style.position = "fixed";
    body.style.top = `${-rootScrollY}px`;
    body.style.left = "0";
    body.style.width = "100%";
    body.style.overflowY = "hidden";
    body.style.overscrollBehaviorY = "none";
    root.style.overflowY = "hidden";
    root.style.overscrollBehaviorY = "none";

    const lockedElements: Array<{
      element: HTMLElement;
      overflowY: string;
      overscrollBehaviorY: string;
      scrollTop: number;
      scrollLeft: number;
    }> = [];
    let ancestor = inputContainerRef.current.parentElement;

    while (ancestor && ancestor !== document.body) {
      const overflowY = window.getComputedStyle(ancestor).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        lockedElements.push({
          element: ancestor,
          overflowY: ancestor.style.overflowY,
          overscrollBehaviorY: ancestor.style.overscrollBehaviorY,
          scrollTop: ancestor.scrollTop,
          scrollLeft: ancestor.scrollLeft,
        });
        ancestor.style.overflowY = "hidden";
        ancestor.style.overscrollBehaviorY = "none";
      }
      ancestor = ancestor.parentElement;
    }

    return () => {
      lockedElements.forEach((locked) => {
        locked.element.style.overflowY = locked.overflowY;
        locked.element.style.overscrollBehaviorY =
          locked.overscrollBehaviorY;
        locked.element.scrollTop = locked.scrollTop;
        locked.element.scrollLeft = locked.scrollLeft;
      });

      body.style.position = bodyStyles.position;
      body.style.top = bodyStyles.top;
      body.style.left = bodyStyles.left;
      body.style.width = bodyStyles.width;
      body.style.overflowY = bodyStyles.overflowY;
      root.style.overflowY = rootStyles.overflowY;
      body.style.overscrollBehaviorY = bodyStyles.overscrollBehaviorY;
      root.style.overscrollBehaviorY = rootStyles.overscrollBehaviorY;
      window.scrollTo(0, rootScrollY);
    };
  }, [showModal]);

  useEffect(() => {
    const parsed = parseTimeValue(propValue);
    setDraftHours(parsed.hours);
    setDraftMinutes(parsed.minutes);
  }, [propValue]);

  const hourOptions = useMemo(
    () =>
      Array.from({ length: 24 }, (_, hour) => {
        const value = String(hour).padStart(2, "0");
        return {
          value,
          label: `${value} giờ`,
        };
      }),
    [],
  );

  const minuteOptions = useMemo(
    () =>
      Array.from({ length: 60 }, (_, minute) => {
        const value = String(minute).padStart(2, "0");
        return {
          value,
          label: `${value} phút`,
        };
      }),
    [],
  );

  const displayValue = useMemo(() => {
    const parsed = parseTimeValue(propValue);
    if (!propValue) return "";
    return `${parsed.hours}:${parsed.minutes}`;
  }, [propValue]);

  const syncDraftFromValue = useCallback(() => {
    const parsed = parseTimeValue(propValue);
    setDraftHours(parsed.hours);
    setDraftMinutes(parsed.minutes);
  }, [propValue]);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    syncDraftFromValue();
    setShowModal(true);
  }, [disabled, syncDraftFromValue]);

  const handleClose = useCallback(() => {
    setShowModal(false);
    syncDraftFromValue();
  }, [syncDraftFromValue]);

  const handleConfirm = useCallback(() => {
    const nextValue = `${draftHours}:${draftMinutes}`;
    onChange?.(buildSyntheticChangeEvent(name, nextValue));
    onBlur?.(buildSyntheticBlurEvent(name, nextValue));
    setShowModal(false);
  }, [draftHours, draftMinutes, name, onBlur, onChange]);

  const handleClear = useCallback(() => {
    onChange?.(buildSyntheticChangeEvent(name, ""));
    onBlur?.(buildSyntheticBlurEvent(name, ""));
    setShowModal(false);
  }, [name, onBlur, onChange]);

  return (
    <div ref={inputContainerRef} className="contents">
      <BaseInput
        ref={ref}
        id={id}
        name={name}
        label={label}
        value={displayValue}
        readOnly
        onClick={handleOpen}
        placeholder={placeholder || "Chọn giờ (24h)"}
        error={error}
        helperText={helperText}
        disabled={disabled}
        className={cn("cursor-pointer", className)}
        containerClassName={containerClassName}
        labelClassName={labelClassName}
        required={required}
        rightIcon={
          <button
            type="button"
            onClick={handleOpen}
            className="focus:outline-none"
            aria-label="Mở bộ chọn giờ"
          >
            <OrganizerInputClock9 className="h-5 w-5 text-Color-Icon-default" />
          </button>
        }
      />

      {showModal && typeof document !== "undefined"
        ? createPortal(
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-3">
              <button
                type="button"
                className="absolute inset-0 [touch-action:none] cursor-default border-none bg-black/20 backdrop-blur-sm"
                onClick={handleClose}
                aria-label="Đóng bộ chọn giờ"
              />

              <div className="relative flex w-full max-w-[20rem] flex-col gap-3 rounded-3xl border border-Color-Stroke-neutral bg-Color-Background-neutral-default_primary p-3 shadow-[0_14px_34px_-18px_rgba(0,0,0,0.45)]">
                <div className="space-y-1">
                  <div className="text-base font-semibold text-Color-Text-neutral-black font-['Be_Vietnam_Pro']">
                    Chọn giờ bắt đầu
                  </div>
                  <div className="text-sm text-Color-Text-neutral-supplement font-['Be_Vietnam_Pro']">
                    Sử dụng định dạng 24 giờ, ví dụ 08:30 hoặc 19:45.
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <div className="text-sm text-Color-Text-neutral-black font-['Be_Vietnam_Pro']">
                      Giờ
                    </div>
                    <WheelPicker
                      options={hourOptions}
                      selectedValue={draftHours}
                      onValueChange={setDraftHours}
                      height={164}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm text-Color-Text-neutral-black font-['Be_Vietnam_Pro']">
                      Phút
                    </div>
                    <WheelPicker
                      options={minuteOptions}
                      selectedValue={draftMinutes}
                      onValueChange={setDraftMinutes}
                      height={164}
                    />
                  </div>
                </div>

                <div className="rounded-xl bg-Color-Background-neutral-default_secondary px-4 py-3 text-center text-lg font-semibold text-Color-Text-neutral-black font-['Be_Vietnam_Pro']">
                  {draftHours}:{draftMinutes}
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="md"
                    className="flex-1"
                    onClick={handleClear}
                  >
                    Xóa giờ
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    className="flex-1"
                    onClick={handleClose}
                  >
                    Hủy
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="flex-1"
                    onClick={handleConfirm}
                  >
                    Chọn
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
});

TimeInput.displayName = "TimeInput";

export { TimeInput };
